import { LocalOverrideAdapter } from "@statsig/js-local-overrides";
import { http } from "./http";
import {
  PROD,
  STAT_SIG_API_URL,
  STAT_SIG_CLIENT_KEY,
  STATSIG_SEGMENT_ADD_USER_ENDPOINT,
} from "./config";
import type { StatsigUser } from "@statsig/js-client";
import { StatsigClient } from "@statsig/js-client";
import { useGateValue } from "@statsig/react-bindings";
import { ENV } from "./hostEnv";
import type { EXP_EVENTS, GateConfig, SYSTEM_EVENTS } from "./featuresGates";
import type { ExperimentConfig } from "./featuresExperiments";

export const overrideAdapter = new LocalOverrideAdapter(STAT_SIG_CLIENT_KEY);

export const statsig = new StatsigClient(
  STAT_SIG_CLIENT_KEY,
  {},
  {
    environment: { tier: ENV.NAME },
    loggingIntervalMs: ENV.NAME === PROD ? 5000 : 1000,
    networkConfig: {
      api: STAT_SIG_API_URL,
    },
    overrideAdapter,
  },
);

interface StatsigUserProperties {
  projectId?: string;

  email?: string;
  userId?: string;

  custom?: StatsigUser["custom"];
}

export async function updateStatsigUser(properties: StatsigUserProperties) {
  const { projectId, email, userId, custom } = properties;

  const { user = {} } = statsig.getContext();

  try {
    await statsig.updateUserAsync({
      ...user,

      // (StatSig's userID) = (web-cloud projectId), NOT userId. See README
      ...(projectId ? { userID: projectId } : {}),
      ...(email ? { email } : {}),
      ...(userId ? { customIDs: { timescaleUserId: userId } } : {}),

      custom: { ...user?.custom, ...custom },
    });
  } catch (error) {
    console.warn("Failed to update Statsig user:", error);
  }
}

// ── Typed helpers ──────────────────────────────────────────────────────

/** Check if a feature gate is enabled (non-reactive, for use outside components). */
export function isGateEnabled(gate: GateConfig): boolean {
  return statsig.checkGate(gate.GATE);
}

/**
 * Reactive hook for checking a feature gate inside components.
 * Prefer this over `isGateEnabled` in React components so they re-render
 * when the gate value changes.
 */
export function useGateEnabled(gate: GateConfig): boolean {
  return useGateValue(gate.GATE);
}

/** Read an experiment parameter value with a typed default. */
export function getExperimentParam<T extends string | number | boolean>(
  experiment: ExperimentConfig,
  param: string,
  defaultValue: T,
): T {
  return statsig.getExperiment(experiment.ID).get(param, defaultValue) as T;
}

/** Log a Statsig event with optional value and metadata. */
export function logStatsigEvent(
  eventName: EXP_EVENTS | SYSTEM_EVENTS | (string & {}),
  value?: string,
  metadata?: Record<string, string>,
) {
  statsig.logEvent(eventName, value, metadata);
}

interface AddUserToStatsigSegmentRequest {
  segmentID: string;
  userID: string;
}

interface AddUserToStatsigSegmentResponse {
  success: boolean;
  segmentID: string;
  userID: string;
}

export const addUserToStatsigSegment = async ({
  segmentID,
  userID,
}: AddUserToStatsigSegmentRequest) => {
  const data = await http.json<AddUserToStatsigSegmentResponse>(
    `${ENV.WEB_ENDPOINT}/${STATSIG_SEGMENT_ADD_USER_ENDPOINT}`,
    {
      method: "POST",
      json: { segmentID, userID },
      keepalive: true,
    },
  );

  if (!data?.success) {
    throw new Error("Statsig segment add-user request failed");
  }

  return data;
};
